import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import StepHeader from '../components/StepHeader';
import DontLose from '../../../assets/images/unsubscribe/dont_lose.webp';
import p13 from '../../../assets/images/unsubscribe/p13.svg';
import p14 from '../../../assets/images/unsubscribe/p14.svg';
import p15 from '../../../assets/images/unsubscribe/p15.svg';
import p16 from '../../../assets/images/unsubscribe/p16.svg';
import p17 from '../../../assets/images/unsubscribe/p17.svg';
import checkmark from '../../../assets/images/unsubscribe/checkmark.svg';

import { useAppState } from '../../../providers/AppProvider.hooks';
import { useCallback, useState } from 'react';
import { APP_CONSTANTS } from '../../../utils/constants';
import { cancelSubscription, trackEvent } from '../../../services/requests';

const points = [
  {
    title: 'Growth Insights',
    subTitle: 'Improve your engagement',
    img: p13,
  },
  {
    title: 'Latest IG Algorithm Secrets',
    subTitle: 'Stay ahead of the curve',
    img: p14,
  },
  {
    title: 'Strategies for Success ',
    subTitle: 'Grow your audience',
    img: p15,
  },
  {
    title: 'Content Made Easy',
    subTitle: 'Make your posts stand out',
    img: p16,
  },
  {
    title: 'Make Every Move Count',
    subTitle: 'Decisions that drive real results',
    img: p17,
  },
];

function Step9({ setStep }: { setStep: React.Dispatch<React.SetStateAction<number>> }) {
  const { setUnsubscribeOpen } = useAppState();
  const [isUsubscribed, setUnsubscribed] = useState(false);
  const { subscriptionId, checkUserSubscription, firebaseUserCredential, isHasGiftOneMonthSubscription } =
    useAppState();
  const supportEmail = APP_CONSTANTS.SUPPORT;
  const theme = useTheme();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleContactUs = () => {
    window.open(`mailto:${supportEmail}`, '_self');
  };

  const handleCancelSubscription = () => {
    if (isLoading) return;
    trackEvent('Cancel subscription click', { host: window.location.host, project: 'metricgurus' }).catch(
      console.error
    );

    if (subscriptionId && firebaseUserCredential?.uid) {
      setLoading(true);
      cancelSubscription(subscriptionId)
        .then(async (response) => {
          if ('error' in response) {
            setError(response.error);
            trackEvent('Cancel subscription error', {
              error: JSON.stringify(response.error),
              subscription_id: subscriptionId,
              host: window.location.host,
              project: 'metricgurus',
            }).catch(console.error);
          } else {
            if (response.success) {
              setUnsubscribed(true);
              await checkUserSubscription(firebaseUserCredential.uid);
              trackEvent('Cancel subscription success', {
                subscription_id: subscriptionId,
                host: window.location.host,
                project: 'metricgurus',
              }).catch(console.error);
            } else {
              setError('Something went wrong');
              trackEvent('Cancel subscription unknown response', {
                response: JSON.stringify(response),
                subscription_id: subscriptionId,
                host: window.location.host,
                project: 'metricgurus',
              }).catch(console.error);
            }
          }
        })
        .catch((error) => {
          console.error('cancelSubscriptions error', error);
          const errorMessage = typeof error === 'string' ? error : error.error ?? error.message;
          setError(typeof errorMessage === 'string' ? errorMessage : 'Something went wrong');

          trackEvent('Cancel subscription request error', {
            error: JSON.stringify(error),
            errorMessage,
            subscription_id: subscriptionId,
            host: window.location.host,
            project: 'metricgurus',
          }).catch(console.error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setError('Cant find subscription id or firebase uid');
      trackEvent('Cant find subscription id or firebase uid', {
        error: 'Cant find subscription id or firebase uid',
        subscription_id: subscriptionId,
        host: window.location.host,
        project: 'metricgurus',
      }).catch(console.error);
    }
  };

  const handleBack = useCallback(() => {
    if (isHasGiftOneMonthSubscription) {
      setStep((prev) => prev - 2);
    } else {
      setStep((prev) => prev - 1);
    }
  }, [setStep, isHasGiftOneMonthSubscription]);

  return (
    <Box>
      <StepHeader onClick={handleBack} title="Don’t lose" />
      <Box
        sx={{
          padding: '20px 20px 0px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            img: {
              width: '100%',
            },
          }}
        >
          <img src={DontLose} alt="Offer"></img>
        </Box>
        <Typography
          sx={{
            fontFamily: 'Avenir Next',
            fontWeight: 600,
            lineHeight: 'normal',
            fontSize: '24px',
            color: theme.palette.text.primary,
            textAlign: 'center',
            marginTop: '20px',
            marginBottom: '5px',
            span: {
              color: '#F63C47',
            },
          }}
        >
          You're about to <span>lose it all</span>
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Avenir Next',
            fontWeight: 400,
            lineHeight: 'normal',
            fontSize: '16px',
            color: theme.palette.text.secondary,
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          Before you finalize your cancellation, here’s what you’ll be leaving behind:
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            width: '100%',
            marginBottom: '20px',
          }}
        >
          {points.map((el, i) => (
            <Box
              key={i}
              sx={{
                background: 'rgba(246, 60, 71, 0.07)',
                borderRadius: '16px',
                padding: '15px',
                position: 'relative',
                display: 'flex',
                gap: '15px',
                alignItems: 'center',
              }}
            >
              <img src={el.img} alt="point"></img>
              <Box sx={{ width: '100%' }}>
                <Typography
                  sx={{
                    fontFamily: 'Avenir Next',
                    fontWeight: 600,
                    lineHeight: 'normal',
                    fontSize: '16px',
                    color: theme.palette.text.primary,
                  }}
                >
                  {el.title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Avenir Next',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    fontSize: '14px',
                    color: theme.palette.text.secondary,
                  }}
                >
                  {el.subTitle}
                </Typography>
              </Box>
              <img width={24} src={checkmark} alt="decor" />
            </Box>
          ))}
        </Box>

        {error && (
          <span
            style={{
              color: 'red',
              marginBottom: '15px',
            }}
          >
            {error}
          </span>
        )}

        <Button
          onClick={() => {
            setUnsubscribeOpen(false);
          }}
          sx={{
            border: 'none',
            borderRadius: '12px',
            background: theme.palette.common.blue,
            color: '#fff',
            height: '60px',
            fontWeight: 600,
            lineHeight: 'normal',
            fontSize: '16px',
            width: '100%',
            cursor: 'pointer',
            textTransform: 'initial',
            fontFamily: 'Avenir Next',
            transition: 'all 0.3s ease',
            WebkitTapHighlightColor: 'transparent',
            '&:hover': {
              background: theme.palette.common.blue,
            },
          }}
        >
          Stay a premium user
        </Button>
        <Typography
          onClick={handleCancelSubscription}
          sx={{
            fontSize: '14px',
            color: theme.palette.text.secondary,
            textDecoration: 'underline',
            textDecorationColor: theme.palette.text.secondary,
            textDecorationThickness: '1px',
            fontWeight: 400,
            fontFamily: 'Avenir Next',
            lineHeight: 'normal',
            margin: '15px auto 20px',
            textAlign: 'center',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {isLoading ? (
            <CircularProgress
              size={16}
              sx={{
                color: theme.palette.text.secondary,
              }}
            />
          ) : (
            'Give up on my IG & cancel'
          )}
        </Typography>
      </Box>

      {isUsubscribed && (
        <Box
          sx={{
            position: 'fixed',
            zIndex: 9999,
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            background: '#23263160',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              background: '#FFF',
              borderRadius: '20px 20px 0px 0px',
              boxShadow: '0px 5px 15px 0px rgba(106, 126, 149, 0.05)',
              padding: '30px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: '500px',
            }}
          >
            <img width={82} src={checkmark} alt="checkmark"></img>

            <Typography
              sx={{
                fontFamily: 'Avenir Next',
                fontWeight: 600,
                lineHeight: 'normal',
                fontSize: '20px',
                color: theme.palette.text.primary,
                marginTop: '20px',
                marginBottom: '10px',
                textAlign: 'center',
              }}
            >
              Your membership has been canceled
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Avenir Next',
                fontWeight: 400,
                lineHeight: 'normal',
                fontSize: '16px',
                color: theme.palette.text.secondary,
                marginBottom: '20px',
                textAlign: 'center',
              }}
            >
              We'll miss you! Truly appreciate you being part of our community.
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Avenir Next',
                fontWeight: 400,
                lineHeight: 'normal',
                fontSize: '14px',
                color: theme.palette.text.primary,
                textAlign: 'center',
                background: '#F5F5F5',
                borderRadius: '20px',
                padding: '20px',
                marginBottom: '20px',
                span: {
                  fontWeight: 600,
                  color: '#56AC14',
                },
              }}
            >
              While your subscription has ended, your Instagram growth doesn’t have to. If you ever want to come back,
              we’d love to welcome you at a <span>special discounted price.</span>
            </Typography>

            <Button
              onClick={() => {
                setUnsubscribeOpen(false);
              }}
              sx={{
                border: 'none',
                borderRadius: '12px',
                background: theme.palette.common.blue,
                color: '#fff',
                height: '60px',
                fontWeight: 600,
                lineHeight: 'normal',
                fontSize: '16px',
                width: '100%',
                cursor: 'pointer',
                textTransform: 'initial',
                fontFamily: 'Avenir Next',
                transition: 'all 0.3s ease',
                WebkitTapHighlightColor: 'transparent',
                '&:hover': {
                  background: theme.palette.common.blue,
                },
              }}
            >
              Back to home page
            </Button>
            <Button
              onClick={handleContactUs}
              sx={{
                marginTop: '10px',
                border: 'none',
                borderRadius: '12px',
                background: '#F5F5F5',
                color: theme.palette.text.primary,
                height: '60px',
                fontWeight: 600,
                lineHeight: 'normal',
                fontSize: '16px',
                width: '100%',
                cursor: 'pointer',
                textTransform: 'initial',
                fontFamily: 'Avenir Next',
                transition: 'all 0.3s ease',
                WebkitTapHighlightColor: 'transparent',
                '&:hover': {
                  background: '#F5F5F5',
                },
              }}
            >
              Contact us
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default Step9;
